import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface VariantAttribute {
  id: string;
  name: string;
  values: string[];
  productCount: number;
}

interface ProductVariant {
  id: string;
  productName: string;
  sku: string;
  attributes: { name: string; value: string }[];
  price: number;
  stock: number;
  isActive: boolean;
}

const MOCK_ATTRIBUTES: VariantAttribute[] = [
  { id: "1", name: "Size", values: ["XS", "S", "M", "L", "XL", "XXL"], productCount: 45 },
  { id: "2", name: "Color", values: ["Red", "Blue", "Green", "Black", "White", "Yellow"], productCount: 78 },
  { id: "3", name: "Material", values: ["Cotton", "Polyester", "Leather", "Wool"], productCount: 23 },
  { id: "4", name: "Storage", values: ["64GB", "128GB", "256GB", "512GB", "1TB"], productCount: 12 },
];

const MOCK_VARIANTS: ProductVariant[] = [
  { id: "1", productName: "Cotton T-Shirt", sku: "CLTH-001-S-RED", attributes: [{ name: "Size", value: "S" }, { name: "Color", value: "Red" }], price: 24.99, stock: 15, isActive: true },
  { id: "2", productName: "Cotton T-Shirt", sku: "CLTH-001-M-RED", attributes: [{ name: "Size", value: "M" }, { name: "Color", value: "Red" }], price: 24.99, stock: 20, isActive: true },
  { id: "3", productName: "Cotton T-Shirt", sku: "CLTH-001-L-BLUE", attributes: [{ name: "Size", value: "L" }, { name: "Color", value: "Blue" }], price: 24.99, stock: 8, isActive: true },
  { id: "4", productName: "Premium Headphones", sku: "ELEC-001-BLK", attributes: [{ name: "Color", value: "Black" }], price: 149.99, stock: 25, isActive: true },
  { id: "5", productName: "Premium Headphones", sku: "ELEC-001-WHT", attributes: [{ name: "Color", value: "White" }], price: 149.99, stock: 18, isActive: true },
  { id: "6", productName: "iPhone Case", sku: "ACC-002-BLU", attributes: [{ name: "Color", value: "Blue" }, { name: "Material", value: "Leather" }], price: 29.99, stock: 0, isActive: false },
];

export default function VariantsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState<"attributes" | "variants">("attributes");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredVariants = MOCK_VARIANTS.filter(
    (v) =>
      v.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posProducts.variants.title")}</h1>
          <p className={themeClasses.textSecondary}>{t("posProducts.variants.subtitle")}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2"
        >
          <i className="ri-add-line"></i>
          {activeTab === "attributes"
            ? t("posProducts.variants.addAttribute")
            : t("posProducts.variants.addVariant")}
        </button>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`border-b ${themeClasses.border} p-1 flex gap-1`}>
          <button
            onClick={() => setActiveTab("attributes")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
              activeTab === "attributes"
                ? "bg-primary-500 text-gray-900"
                : `${themeClasses.text} hover:bg-dark-card`
            }`}
          >
            <i className="ri-settings-3-line"></i>
            {t("posProducts.variants.attributes")}
          </button>
          <button
            onClick={() => setActiveTab("variants")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
              activeTab === "variants"
                ? "bg-primary-500 text-gray-900"
                : `${themeClasses.text} hover:bg-dark-card`
            }`}
          >
            <i className="ri-stack-line"></i>
            {t("posProducts.variants.productVariants")}
          </button>
        </div>

        {activeTab === "attributes" ? (
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {MOCK_ATTRIBUTES.map((attr) => (
                <div
                  key={attr.id}
                  className={`p-4 rounded-xl border ${themeClasses.border} ${themeClasses.cardBg}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className={`font-semibold ${themeClasses.text}`}>{attr.name}</h3>
                      <p className={`text-sm ${themeClasses.textSecondary}`}>
                        {attr.productCount} {t("posProducts.variants.productsUsing")}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button aria-label={t("general.edit", { ns: "common" })} className={`p-1.5 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                        <i className="ri-edit-line"></i>
                      </button>
                      <button aria-label={t("general.delete", { ns: "common" })} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500">
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {attr.values.map((value) => (
                      <span
                        key={value}
                        className={`px-2 py-1 rounded-lg text-sm ${themeClasses.bg} border ${themeClasses.border} ${themeClasses.text}`}
                      >
                        {value}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className={`p-4 border-b ${themeClasses.border}`}>
              <div className="relative max-w-md">
                <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
                <input
                  type="text"
                  placeholder={t("posProducts.variants.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("posProducts.product")}
                    </th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>SKU</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("posProducts.variants.attributes")}
                    </th>
                    <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("posProducts.price")}
                    </th>
                    <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("posProducts.stock")}
                    </th>
                    <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("posProducts.status")}
                    </th>
                    <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>
                      {t("posProducts.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVariants.map((variant) => (
                    <tr key={variant.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                      <td className={`px-4 py-3 font-medium ${themeClasses.text}`}>{variant.productName}</td>
                      <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>{variant.sku}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {variant.attributes.map((attr, i) => (
                            <span
                              key={i}
                              className={`px-2 py-0.5 rounded text-xs ${themeClasses.cardBg} ${themeClasses.text}`}
                            >
                              {attr.name}: {attr.value}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${themeClasses.text}`}>
                        ${variant.price.toFixed(2)}
                      </td>
                      <td className={`px-4 py-3 text-right ${variant.stock < 10 ? "text-amber-500" : themeClasses.text}`}>
                        {variant.stock}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            variant.isActive ? "bg-emerald-500/20 text-emerald-500" : "bg-gray-500/20 text-gray-500"
                          }`}
                        >
                          {variant.isActive ? t("posProducts.statusActive") : t("posProducts.statusInactive")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button aria-label={t("general.edit", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                            <i className="ri-edit-line"></i>
                          </button>
                          <button aria-label={t("general.delete", { ns: "common" })} className="p-2 rounded-lg hover:bg-red-500/10 text-red-500">
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.bg} rounded-2xl p-6 w-full max-w-md`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-bold ${themeClasses.text}`}>
                {activeTab === "attributes"
                  ? t("posProducts.variants.addAttribute")
                  : t("posProducts.variants.addVariant")}
              </h2>
              <button onClick={() => setShowCreateModal(false)} className={themeClasses.textSecondary}>
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            {activeTab === "attributes" ? (
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                    {t("posProducts.variants.attributeName")}
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Size, Color"
                    className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                    {t("posProducts.variants.values")}
                  </label>
                  <input
                    type="text"
                    placeholder={t("posProducts.variants.valuesPlaceholder")}
                    className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                  />
                  <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                    {t("posProducts.variants.valuesHint")}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                    {t("posProducts.product")}
                  </label>
                  <select
                    className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
                  >
                    <option>{t("posProducts.variants.selectProduct")}</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                    {t("posProducts.variants.selectAttributes")}
                  </label>
                  <p className={`text-sm ${themeClasses.textSecondary}`}>
                    {t("posProducts.variants.selectAttributesHint")}
                  </p>
                </div>
              </div>
            )}
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className={`flex-1 px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text}`}
              >
                {t("common.cancel")}
              </button>
              <button className="flex-1 px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium">
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
